import React, { useEffect, useState } from 'react';
import { Col, Container, Row, Spinner } from 'react-bootstrap';
import { IoChevronBackOutline } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Button from '../../../Component/Button/Button';
import CustomPhoneInput from '../../../Component/CustomPhoneInput';
import DropDown from '../../../Component/DropDown/DropDown';
import Input from '../../../Component/Input/Input';
import { ProfileWithEditButton } from '../../../Component/ProfileWithEditButton';
import SideBarSkeleton from '../../../Component/SideBarSkeleton';
import {
  CreateFormData,
  convertArrayKeysToObject,
  formatLabel,
  validateEmail
} from '../../../config/apiUrl';
import { useAddOrEditAgentMutation, useGetAgentQuery } from '../../../store/apiSlice/apiSlice';
import classes from './AddAgentPage.module.css';

import { useTranslation } from 'react-i18next';
import { MdAutoAwesome } from 'react-icons/md';
import validator from 'validator';
import FormLanguages from '../../../Component/FormLanguages';
import { Loader } from '../../../Component/Loader';
import QuillInput from '../../../Component/QuillInput';
import {
  allLanguagesArray,
  designationArray,
  languagesOptions
} from '../../../constant/commonData';

function AddAgentPage() {
  const navigate = useNavigate();
  const slug = useParams()?.slug;
  const languagesKeysObject = convertArrayKeysToObject(languagesOptions?.map((e) => e?.value));
  const [language, setLanguage] = useState(languagesOptions[0]);
  const [autoFillLoading, setAutoFillLoading] = useState(false);
  const { t } = useTranslation(['dashboardAgents', 'locationComp'], {
    lng: language?.value
  });

  const [formState, setFormState] = useState({
    image: null,
    agentName: languagesKeysObject,
    agentDesignation: '',
    serviceAreas: languagesKeysObject,
    languagesWeSpeak: [],
    registrationNumber: '',
    description: languagesKeysObject,
    email: '',
    whatsappNumber: '',
    phone: ''
  });

  const { data, isLoading: isLoadingGet } = useGetAgentQuery(
    {
      slug
    },
    {
      skip: !slug
    }
  );

  const [addOrEditFn, { isLoading: loadingApi }] = useAddOrEditAgentMutation({});

  useEffect(() => {
    let specificAgent = data?.data;

    if (specificAgent) {
      setFormState({
        image: specificAgent?.photo,
        agentName: specificAgent?.agentName,
        agentDesignation: specificAgent?.agentDesignation,
        serviceAreas: specificAgent?.serviceAreas,
        languagesWeSpeak: specificAgent?.language,
        registrationNumber: specificAgent?.registrationNumber,
        description: specificAgent?.description,
        email: specificAgent?.email,
        whatsappNumber: specificAgent?.whatsapp,
        phone: specificAgent?.phoneNumber
      });
    }
  }, [data]);

  const validateParams = () => {
    const multiLanguageParams = {
      agentName: formState?.agentName,
      serviceAreas: formState?.serviceAreas,
      description: formState?.description
    };
    const params = {
      photo: formState?.image,
      agentDesignation: formState?.agentDesignation,
      language: formState?.languagesWeSpeak,
      registrationNumber: formState?.registrationNumber,
      email: formState?.email,
      phoneNumber: formState?.phone,
      whatsapp: formState?.whatsappNumber
    };

    for (let lang in languagesKeysObject) {
      // validate multiLanguageParams
      for (let key in multiLanguageParams) {
        if (!multiLanguageParams[key][lang]) {
          const formKey = `form.${key}`;
          toast.error(`${t('toastError.t1')} ${t(formKey)} ${t('toastError.t3')} "${lang}".`);
          return [false, null];
        }
      }

      // validate params
      for (let key in params) {
        if (!params[key]) {
          const formKey = `form.${key}`;
          toast.error(`${t('toastError.t1')} ${t(formKey)} ${t('toastError.t2')}.`);
          return [false, null];
        }

        // if key is a url
        if (key.includes('Link') || key.includes('link')) {
          if (typeof params[key] == 'object') {
            return [true, { ...params, ...multiLanguageParams }];
          }
          if (!validator.isURL(params[key])) {
            toast.error(`${t('toastError.t4')} ${formatLabel(key)}.`);
            return [false, null];
          }
        }

        if (Array.isArray(params[key])) {
          if (params[key].length === 0) {
            const formKey = `form.${key}`;
            toast.error(`${t('toastError.t1')} ${t(formKey)} ${t('toastError.t2')}.`);
            return [false, null];
          }
        }

        // if key is email, validate email
        if (key === 'email') {
          if (!validateEmail(params[key])) {
            toast.error(`${t('toastError.t4')} ${formatLabel(key)}.`);
            return [false, null];
          }
        }

        // whatsapp, phoneNumber => must be a valid phone number
        if (key === 'whatsapp' || key === 'phoneNumber') {
          if (!validator.isMobilePhone(params[key])) {
            toast.error(`${t('toastError.t4')} ${formatLabel(key)}.`);
            return [false, null];
          }
        }
      }
    }

    return [true, { ...params, ...multiLanguageParams }];
  };

  const handleAutoFill = async () => {
    setAutoFillLoading(true);
    const multiLanguageFields = {
      title: formState?.agentName,
      tagline: formState?.serviceAreas,
      description: formState?.description
    };

    const currentLang = language?.value;
    const langLabel = languagesOptions?.find((e) => e?.value === currentLang)?.label ?? 'Unknown';

    for (const key in multiLanguageFields) {
      const value = multiLanguageFields[key]?.[currentLang]?.trim();
      const label = formatLabel(key);
      if (!value) {
        toast.error(
          `Missing field "${label === 'Tagline' ? 'Service Areas ' : label}" in ${langLabel}`
        );
        setAutoFillLoading(false);
        return;
      }
    }

    // Show loading toast
    const loadingToast = toast.loading('Translating fields to other languages...');

    try {
      const response = await fetch(
        'https://turegu-ai-api.devcustomprojects.online/translate/property',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...multiLanguageFields
          }),
          redirect: 'follow'
        }
      );

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      setFormState((prevState) => ({
        ...prevState,
        agentName: {
          ...prevState.agentName,
          ...data?.title
        },
        serviceAreas: {
          ...prevState.serviceAreas,
          ...data?.tagline
        },
        description: {
          ...prevState.description,
          ...data?.description
        }
      }));

      toast.success(`Fields translated successfully for all languages!`);
    } catch (error) {
      console.error('Error translating fields:', error);

      toast.warning('Translation service unavailable.');
    } finally {
      toast.dismiss(loadingToast);
      setAutoFillLoading(false);
    }
  };

  // write a callback hell functions

  // handleSubmit
  const handleSubmit = async () => {
    const [isValid, params] = validateParams();

    if (!isValid) return;
    const formData = CreateFormData(params);
    const response = await addOrEditFn({ data: formData, slug });
    if (response.data) {
      toast.success(`${t('toastError.t5')} ${slug ? t('toastError.t6') : t('toastError.t7')}`);
      navigate(-1);
    }
  };

  return (
    <SideBarSkeleton>
      {isLoadingGet ? (
        <Loader />
      ) : (
        <Container fluid className={classes.page}>
          <Row>
            <Col md={12} className={classes.mb30}>
              <Button
                leftIcon={<IoChevronBackOutline />}
                variant=""
                className={classes.backBtn}
                onClick={() => {
                  navigate('/company/agents-management');
                }}>
                {t('backButton')}
              </Button>
            </Col>
            <Col md={12}>
              <div
                className={[
                  classes.formBg,
                  ['ar', 'fa']?.includes(language?.value) ? 'rtl' : 'ltr'
                ].join(' ')}>
                <Container>
                  <Row className={classes.formRow}>
                    <Col md={6}>
                      <h3 className={classes.title}>
                        {slug ? t('form.titleEdit') : t('form.titleCreate')}{' '}
                      </h3>
                    </Col>
                    <Col md={6}>
                      <FormLanguages
                        value={language}
                        setter={setLanguage}
                        className={classes.languagesMain}
                      />
                    </Col>

                    <Col md={12}>
                      <div className={classes.headAndDropDown}>
                        <h4>{t('form.tagline')}</h4>
                      </div>
                    </Col>
                    <Row className="d-flex justify-content-center align-items-center">
                      <Col md={10}>
                        <p>{t('form.pageDesc')}</p>
                      </Col>
                      <Col md={2}>
                        <ProfileWithEditButton
                          isEdit={true}
                          updateImage={formState.image}
                          setUpdateImage={(e) => setFormState({ ...formState, image: e })}
                          className={classes.profileWithEditClass}
                        />
                      </Col>
                    </Row>
                    <Col md={12} className="d-flex justify-content-between align-items-center">
                      <h4 className={classes.subTitle}>{t('form.formTitle')}</h4>
                      <Button
                        variant="secondary"
                        onClick={handleAutoFill}
                        disabled={autoFillLoading}
                        className="d-flex align-items-center gap-2"
                        customStyle={{
                          width: 'fit-content'
                        }}>
                        {autoFillLoading ? (
                          <Spinner size="sm" className="me-2" />
                        ) : (
                          <MdAutoAwesome size={20} className="me-2" />
                        )}
                        {t('form.autoFill')}
                      </Button>
                    </Col>
                    <Col md={6}>
                      <Input
                        label={t('form.agentName')}
                        placeholder={t('form.agentNamePlaceholder')}
                        variant="dashboard"
                        value={formState.agentName[language?.value]}
                        setValue={(e) =>
                          setFormState({
                            ...formState,
                            agentName: {
                              ...formState?.agentName,
                              [language?.value]: e
                            }
                          })
                        }
                      />
                    </Col>
                    <Col md={6}>
                      <DropDown
                        label={t('form.agentDesignation')}
                        indicatorColor="var(--dashboard-text-gray-clr)"
                        placeholder={t('form.agentDesignationPlaceholder')}
                        variant="dashboard"
                        value={
                          formState?.agentDesignation?.length > 0 ? formState?.agentDesignation : []
                        }
                        setValue={(e) => {
                          setFormState({
                            ...formState,
                            agentDesignation: e
                          });
                        }}
                        options={designationArray}
                        getOptionLabel={(e) => {
                          return e?.[language?.value];
                        }}
                        getOptionValue={(e) => {
                          return e?.[language?.value];
                        }}
                        isMulti={true}
                      />
                    </Col>
                    <Col md={6}>
                      <Input
                        label={t('form.serviceAreas')}
                        placeholder={t('form.serviceAreasPlaceholder')}
                        variant="dashboard"
                        value={formState.serviceAreas[language?.value]}
                        setValue={(e) => {
                          setFormState({
                            ...formState,
                            serviceAreas: {
                              ...formState?.serviceAreas,
                              [language?.value]: e
                            }
                          });
                        }}
                      />
                    </Col>
                    {/* Ram30 k bolne p laga rha hun */}
                    <Col md={6}>
                      <DropDown
                        isMulti
                        label={t('form.languagesWeSpeak')}
                        indicatorColor="var(--dashboard-text-gray-clr)"
                        placeholder={t('form.languageWeSpeakPlaceHolder')}
                        variant="dashboard"
                        value={
                          formState?.languagesWeSpeak?.length > 0 ? formState?.languagesWeSpeak : []
                        }
                        setValue={(e) => {
                          setFormState({
                            ...formState,
                            languagesWeSpeak: e
                          });
                        }}
                        options={allLanguagesArray}
                        getOptionLabel={(e) => {
                          return e?.[language?.value];
                        }}
                        getOptionValue={(e) => {
                          return e?.[language?.value];
                        }}
                      />
                    </Col>
                    <Col md={6}>
                      <Input
                        label={t('form.registrationNumber')}
                        placeholder={t('form.registrationNumber')}
                        variant="dashboard"
                        value={formState.registrationNumber}
                        setValue={(e) => {
                          setFormState({
                            ...formState,
                            registrationNumber: e
                          });
                        }}
                        type={'number'}
                      />
                    </Col>
                    <Col md={12}>
                      <QuillInput
                        key={`description-${language?.value}`}
                        label={t('form.description')}
                        placeholder={t('form.descriptionPlaceholder')}
                        variant="dashboard"
                        value={formState.description[language?.value]}
                        setter={(e) => {
                          if (!autoFillLoading) {
                            setFormState({
                              ...formState,
                              description: {
                                ...formState?.description,
                                [language?.value]: e
                              }
                            });
                          }
                        }}
                      />
                    </Col>
                    <Col md={6}>
                      <Input
                        label={t('form.email')}
                        placeholder={t('form.emailPlaceholder')}
                        variant="dashboard"
                        disabled={slug}
                        value={formState.email}
                        setValue={(e) => {
                          setFormState({
                            ...formState,
                            email: e
                          });
                        }}
                        autocomplete="new-password"
                      />
                    </Col>
                    {/* <Col md={6}>
                    <Input
                      label={'Password'}
                      placeholder={'Password'}
                      variant="dashboard"
                      value={slug ? '**********' : password}
                      disabled={slug ? true : false}
                      setValue={setPassword}
                      type={'password'}
                      autocomplete="new-password"
                    />
                  </Col> */}
                    <Col md={6}>
                      <CustomPhoneInput
                        label={t('form.phone')}
                        placeholder={t('form.phonePlaceholder')}
                        variant="dashboard"
                        value={formState.phone}
                        setter={(e) => {
                          setFormState({
                            ...formState,
                            phone: e
                          });
                        }}
                      />
                    </Col>
                    <Col md={6}>
                      <CustomPhoneInput
                        label={t('form.whatsAppNumber')}
                        placeholder={t('form.whatsAppNumberPlaceholder')}
                        variant="dashboard"
                        value={formState.whatsappNumber}
                        setter={(e) => {
                          setFormState({
                            ...formState,
                            whatsappNumber: e
                          });
                        }}
                      />
                    </Col>

                    <Col md={12} className={classes.btnDiv}>
                      <Button className={classes.btn} disabled={loadingApi}>
                        {t('form.cancelBtn')}
                      </Button>
                      <Button className={classes.btn} onClick={handleSubmit} disabled={loadingApi}>
                        {loadingApi ? t('form.saveBtnLoading') : t('form.saveBtn')}
                      </Button>
                    </Col>
                  </Row>
                </Container>
              </div>
            </Col>
          </Row>
        </Container>
      )}
    </SideBarSkeleton>
  );
}

export default AddAgentPage;
